import * as THREE from "three";
import { Chunk, CHUNK_STATE, CHUNK_X, CHUNK_Z, chunkKey, WORLD_HEIGHT } from "./chunk";
import { TerrainGenerator, SEA_LEVEL } from "./terrain";
import { AIR, BEDROCK, BLOCKS, GRASS, TORCH, WATER, isSolid } from "./blocks";

export interface RaycastHit {
  /** Block coordinates of the hit voxel. */
  x: number;
  y: number;
  z: number;
  /** Face normal pointing out of the hit voxel (the side you can build on). */
  nx: number;
  ny: number;
  nz: number;
  block: number;
  distance: number;
}

/** Implemented by the render layer; injected so World stays renderer-agnostic. */
export interface ChunkMesher {
  mesh(chunk: Chunk, world: World): void;
}

interface Slot {
  cx: number;
  cz: number;
  d2: number;
}

/**
 * Owns every loaded chunk, mediates block access across chunk borders, and
 * streams chunks in and out around the player on a per-frame time budget.
 */
export class World {
  readonly chunks = new Map<string, Chunk>();
  readonly generator: TerrainGenerator;
  readonly seed: number;
  readonly torches = new Set<string>();

  /** Chunk radius kept loaded and meshed around the player. */
  renderDistance = 8;
  /** Extra chunks kept loaded past the render distance so walking doesn't thrash. */
  private unloadPadding = 2;

  private readonly group = new THREE.Group();
  private mesher: ChunkMesher | null = null;
  /**
   * Player edits, grouped by chunk and stored by local block index. Generated
   * terrain is reproducible from the seed, so only these deltas need saving.
   */
  private readonly edits = new Map<string, Map<number, number>>();

  /** Chunk slots around the current centre, sorted nearest first. */
  private genOrder: Slot[] = [];
  private meshOrder: Slot[] = [];
  private orderCx = Number.NaN;
  private orderCz = Number.NaN;

  stats = {
    chunksLoaded: 0,
    chunksMeshed: 0,
    genThisFrame: 0,
    meshBuildsThisFrame: 0,
  };

  constructor(seed: number, mesher?: ChunkMesher) {
    this.seed = seed;
    this.generator = new TerrainGenerator(seed);
    this.group.name = "world";
    if (mesher) this.mesher = mesher;
  }

  setMesher(mesher: ChunkMesher): void {
    this.mesher = mesher;
  }

  get object3D(): THREE.Group {
    return this.group;
  }

  // --- block access -------------------------------------------------------

  /** Read a block, generating the containing chunk on demand. */
  getBlock(wx: number, wy: number, wz: number): number {
    if (wy < 0) return BEDROCK; // solid floor so we never see under the world
    if (wy >= WORLD_HEIGHT) return AIR;
    const chunk = this.getChunk(wx >> 4, wz >> 4);
    if (!chunk) return AIR;
    return chunk.get(wx & 15, wy, wz & 15);
  }

  /** Read without generating. Returns AIR for unloaded chunks. */
  peekBlock(wx: number, wy: number, wz: number): number {
    if (wy < 0 || wy >= WORLD_HEIGHT) return AIR;
    const chunk = this.chunks.get(chunkKey(wx >> 4, wz >> 4));
    if (!chunk) return AIR;
    return chunk.get(wx & 15, wy, wz & 15);
  }

  /** True while the chunk containing this world column is loaded. */
  isChunkLoaded(wx: number, wz: number): boolean {
    return this.chunks.has(chunkKey(wx >> 4, wz >> 4));
  }

  getChunk(cx: number, cz: number): Chunk | undefined {
    const existing = this.chunks.get(chunkKey(cx, cz));
    if (existing) return existing;
    return this.generateChunk(cx, cz);
  }

  private generateChunk(cx: number, cz: number): Chunk {
    const chunk = new Chunk(cx, cz);
    this.generator.generate(chunk);
    this.applyRecordedEdits(chunk);
    this.chunks.set(chunkKey(cx, cz), chunk);
    this.stats.chunksLoaded = this.chunks.size;
    return chunk;
  }

  /**
   * Write a block and mark every chunk whose mesh is affected.
   *
   * `record` is off only for deterministic setup work such as clearing spawn
   * headroom, which must not become a player edit in the save file.
   */
  setBlock(wx: number, wy: number, wz: number, id: number, record = true): boolean {
    if (wy < 1 || wy >= WORLD_HEIGHT) return false;
    const cx = wx >> 4;
    const cz = wz >> 4;
    const chunk = this.getChunk(cx, cz);
    if (!chunk) return false;

    const lx = wx & 15;
    const lz = wz & 15;
    const oldId = chunk.get(lx, wy, lz);
    if (oldId === id) return false;
    if (oldId === TORCH && id !== TORCH) this.torches.delete(`${wx},${wy},${wz}`);
    if (id === TORCH) this.torches.add(`${wx},${wy},${wz}`);
    chunk.set(lx, wy, lz, id);
    chunk.dirty = true;
    chunk.recomputeHeight(lx, lz);
    if (record) this.recordEdit(cx, cz, lx, wy, lz, id);

    if (id === TORCH || oldId === TORCH) {
      for (let dx = -1; dx <= 1; dx++) {
        for (let dz = -1; dz <= 1; dz++) {
          const n = this.chunks.get(chunkKey(cx + dx, cz + dz));
          if (n) n.dirty = true;
        }
      }
    }

    // Border edits change a neighbour's face culling, so remesh those too.
    const nbs: [number, number][] = [];
    if (lx === 0) nbs.push([cx - 1, cz]);
    if (lx === 15) nbs.push([cx + 1, cz]);
    if (lz === 0) nbs.push([cx, cz - 1]);
    if (lz === 15) nbs.push([cx, cz + 1]);
    if (lx === 0 && lz === 0) nbs.push([cx - 1, cz - 1]);
    if (lx === 0 && lz === 15) nbs.push([cx - 1, cz + 1]);
    if (lx === 15 && lz === 0) nbs.push([cx + 1, cz - 1]);
    if (lx === 15 && lz === 15) nbs.push([cx + 1, cz + 1]);
    for (const [nx, nz] of nbs) {
      const n = this.chunks.get(chunkKey(nx, nz));
      if (n) n.dirty = true;
    }

    return true;
  }

  private recordEdit(cx: number, cz: number, lx: number, y: number, lz: number, id: number): void {
    const key = chunkKey(cx, cz);
    let chunkEdits = this.edits.get(key);
    if (!chunkEdits) {
      chunkEdits = new Map();
      this.edits.set(key, chunkEdits);
    }
    chunkEdits.set(Chunk.index(lx, y, lz), id);
  }

  private applyRecordedEdits(chunk: Chunk): void {
    const chunkEdits = this.edits.get(chunkKey(chunk.cx, chunk.cz));
    if (!chunkEdits || chunkEdits.size === 0) return;
    for (const [index, id] of chunkEdits) {
      chunk.blocks[index] = id;
      if (id === TORCH) {
        const lx = index % CHUNK_X;
        const lz = Math.floor(index / CHUNK_X) % CHUNK_Z;
        const y = Math.floor(index / (CHUNK_X * CHUNK_Z));
        this.torches.add(`${chunk.originX + lx},${y},${chunk.originZ + lz}`);
      }
    }
    chunk.recomputeHeights();
  }

  /**
   * Install saved player edits. Call this before the affected chunks generate;
   * chunks that are already loaded are patched in place as well.
   */
  applySavedEdits(blocks: readonly number[]): void {
    this.edits.clear();
    this.torches.clear();
    for (let i = 0; i + 3 < blocks.length; i += 4) {
      const x = blocks[i];
      const y = blocks[i + 1];
      const z = blocks[i + 2];
      const id = blocks[i + 3];
      if (
        !Number.isSafeInteger(x) ||
        !Number.isSafeInteger(y) ||
        !Number.isSafeInteger(z) ||
        !Number.isSafeInteger(id) ||
        Math.abs(x) > 2 ** 30 ||
        Math.abs(z) > 2 ** 30 ||
        y < 1 ||
        y >= WORLD_HEIGHT ||
        id < 0 ||
        id >= BLOCKS.length
      ) {
        continue;
      }
      if (id === TORCH) {
        this.torches.add(`${x},${y},${z}`);
      }
      const cx = Math.floor(x / CHUNK_X);
      const cz = Math.floor(z / CHUNK_Z);
      const lx = x - cx * CHUNK_X;
      const lz = z - cz * CHUNK_Z;
      this.recordEdit(cx, cz, lx, y, lz, id);

      // Patch an already-loaded chunk through the normal write path so border
      // neighbours are remeshed too. Everything else is applied on generation.
      if (this.chunks.has(chunkKey(cx, cz))) {
        this.setBlock(x, y, z, id, false);
      }
    }
  }

  /** Flat [x, y, z, id, ...] snapshot of every player edit, for the save file. */
  getEditSnapshot(): number[] {
    const out: number[] = [];
    for (const [key, chunkEdits] of this.edits) {
      const comma = key.indexOf(",");
      const cx = Number(key.slice(0, comma));
      const cz = Number(key.slice(comma + 1));
      for (const [index, id] of chunkEdits) {
        const lx = index % CHUNK_X;
        const lz = Math.floor(index / CHUNK_X) % CHUNK_Z;
        const y = Math.floor(index / (CHUNK_X * CHUNK_Z));
        out.push(cx * CHUNK_X + lx, y, cz * CHUNK_Z + lz, id);
      }
    }
    return out;
  }

  isSolidAt(wx: number, wy: number, wz: number): boolean {
    return isSolid(this.getBlock(wx, wy, wz));
  }

  /** True when the given coordinate is within distance of an active torch. */
  isNearTorch(wx: number, wy: number, wz: number, maxDist = 10): boolean {
    const maxSq = maxDist * maxDist;
    for (const key of this.torches) {
      const comma1 = key.indexOf(",");
      const comma2 = key.indexOf(",", comma1 + 1);
      const tx = Number(key.slice(0, comma1));
      const ty = Number(key.slice(comma1 + 1, comma2));
      const tz = Number(key.slice(comma2 + 1));
      const dx = wx - tx;
      const dy = wy - ty;
      const dz = wz - tz;
      if (dx * dx + dy * dy + dz * dz <= maxSq) return true;
    }
    return false;
  }

  /** Fractional light intensity [0..1] emitted by nearby torches. */
  getTorchLight(wx: number, wy: number, wz: number, radius = 9): number {
    let best = 0;
    const radSq = radius * radius;
    for (const key of this.torches) {
      const comma1 = key.indexOf(",");
      const comma2 = key.indexOf(",", comma1 + 1);
      const tx = Number(key.slice(0, comma1));
      const ty = Number(key.slice(comma1 + 1, comma2));
      const tz = Number(key.slice(comma2 + 1));
      const dx = wx - tx;
      const dy = wy - ty;
      const dz = wz - tz;
      const dSq = dx * dx + dy * dy + dz * dz;
      if (dSq <= radSq) {
        const d = Math.sqrt(dSq);
        const light = Math.max(0, 1 - d / radius);
        if (light > best) best = light;
      }
    }
    return best;
  }

  // --- streaming ----------------------------------------------------------

  /**
   * Generate and mesh chunks around `focus`, nearest first, spending at most
   * `budgetMs` so the frame never stalls. Work resumes next frame.
   */
  update(focus: THREE.Vector3, budgetMs = 6): void {
    const start = performance.now();
    const pcx = Math.floor(focus.x / CHUNK_X);
    const pcz = Math.floor(focus.z / CHUNK_Z);
    this.refreshOrder(pcx, pcz);

    this.stats.genThisFrame = 0;
    this.stats.meshBuildsThisFrame = 0;

    // Pass 1 — generate. This runs a ring wider than the mesh radius so every
    // meshable chunk has all eight neighbours present.
    for (const slot of this.genOrder) {
      const key = chunkKey(slot.cx, slot.cz);
      if (this.chunks.has(key)) continue;
      this.generateChunk(slot.cx, slot.cz);
      this.stats.genThisFrame++;
      if (performance.now() - start > budgetMs * 0.55) break;
    }

    // Pass 2 — mesh dirty chunks that have all eight neighbours present.
    for (const slot of this.meshOrder) {
      const chunk = this.chunks.get(chunkKey(slot.cx, slot.cz));
      if (!chunk || !chunk.dirty) continue;
      if (!this.hasNeighbours(slot.cx, slot.cz)) {
        chunk.awaitingNeighbours = true;
        continue;
      }
      chunk.awaitingNeighbours = false;
      chunk.dirty = false;
      this.mesher?.mesh(chunk, this);
      chunk.state = CHUNK_STATE.MESHED;
      this.stats.meshBuildsThisFrame++;
      this.stats.chunksMeshed++;
      if (performance.now() - start > budgetMs) return;
    }
  }

  private refreshOrder(pcx: number, pcz: number): void {
    if (pcx === this.orderCx && pcz === this.orderCz && this.genOrder.length) {
      // Same centre: evict anything that fell out of the keep radius.
      this.evictDistant(pcx, pcz, this.renderDistance + this.unloadPadding);
      return;
    }

    this.orderCx = pcx;
    this.orderCz = pcz;
    const r = this.renderDistance;

    // The mesh set is a disc; the generate set is the square ring around it.
    // A disc dilated by one is not a disc, so a square is the only shape that
    // reliably covers all eight neighbours of the boundary.
    this.meshOrder = [];
    for (let dz = -r; dz <= r; dz++) {
      for (let dx = -r; dx <= r; dx++) {
        if (dx * dx + dz * dz > r * r) continue;
        this.meshOrder.push({ cx: pcx + dx, cz: pcz + dz, d2: dx * dx + dz * dz });
      }
    }
    this.meshOrder.sort((a, b) => a.d2 - b.d2);

    this.genOrder = [];
    const gr = r + 1;
    for (let dz = -gr; dz <= gr; dz++) {
      for (let dx = -gr; dx <= gr; dx++) {
        this.genOrder.push({ cx: pcx + dx, cz: pcz + dz, d2: dx * dx + dz * dz });
      }
    }
    this.genOrder.sort((a, b) => a.d2 - b.d2);

    this.evictDistant(pcx, pcz, r + this.unloadPadding);
  }

  private hasNeighbours(cx: number, cz: number): boolean {
    for (let dz = -1; dz <= 1; dz++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (dx === 0 && dz === 0) continue;
        const c = this.chunks.get(chunkKey(cx + dx, cz + dz));
        if (!c || c.state === CHUNK_STATE.EMPTY) return false;
      }
    }
    return true;
  }

  private evictDistant(pc: number, pz: number, limit: number): void {
    const limit2 = limit * limit;
    for (const [key, chunk] of this.chunks) {
      const dx = chunk.cx - pc;
      const dz = chunk.cz - pz;
      if (dx * dx + dz * dz <= limit2) continue;
      chunk.dispose();
      this.chunks.delete(key);
    }
    this.stats.chunksLoaded = this.chunks.size;
  }

  // --- queries ------------------------------------------------------------

  /** Highest non-air, non-water block y in a world column, or -1 if empty. */
  surfaceHeight(wx: number, wz: number): number {
    const chunk = this.getChunk(wx >> 4, wz >> 4);
    if (!chunk) return -1;
    const lx = wx & 15;
    const lz = wz & 15;
    for (let y = WORLD_HEIGHT - 1; y >= 0; y--) {
      const b = chunk.get(lx, y, lz);
      if (b !== AIR && b !== WATER) return y;
    }
    return -1;
  }

  /**
   * Find a safe standing position near the requested column.
   *
   * Prefers open, grassy lowlands — starting the player on a bare mountain or in
   * a cave is technically valid but a miserable first frame. Scans outward in
   * rings; if nothing suitable exists it falls back to the best solid column
   * found and carves headroom so the player is never spawned inside terrain.
   */
  findSpawn(nearX: number, nearZ: number): THREE.Vector3 {
    const SEARCH = 256;
    let best: { x: number; z: number; top: number } | null = null;

    for (let radius = 0; radius <= SEARCH; radius += 4) {
      for (let i = -radius; i <= radius; i += 2) {
        const candidates: [number, number][] =
          radius === 0
            ? [[nearX, nearZ]]
            : [
                [nearX + i, nearZ + radius],
                [nearX + i, nearZ - radius],
                [nearX + radius, nearZ + i],
                [nearX - radius, nearZ + i],
              ];

        for (const [x, z] of candidates) {
          const top = this.surfaceHeight(x, z);
          if (top < 1) continue;
          const ground = this.getBlock(x, top, z);
          if (!isSolid(ground)) continue;

          const clear =
            !isSolid(this.getBlock(x, top + 1, z)) && !isSolid(this.getBlock(x, top + 2, z));

          if (clear && top >= SEA_LEVEL && ground === GRASS) {
            return new THREE.Vector3(x + 0.5, top + 1, z + 0.5);
          }
          if (clear && top >= SEA_LEVEL && !best) best = { x, z, top };
          if (!best || top > best.top) best = { x, z, top };
        }
      }
    }

    if (best) {
      for (let y = best.top + 1; y <= best.top + 2; y++) {
        this.setBlock(best.x, y, best.z, AIR, false);
      }
      return new THREE.Vector3(best.x + 0.5, best.top + 1, best.z + 0.5);
    }

    // Nothing solid anywhere nearby — should not happen, but never return NaN.
    return new THREE.Vector3(nearX + 0.5, SEA_LEVEL + 6, nearZ + 0.5);
  }

  /**
   * Amanatides & Woo voxel traversal. Returns the first block passing `filter`
   * (default: any solid block) within `maxDist`.
   */
  raycast(
    origin: THREE.Vector3,
    dir: THREE.Vector3,
    maxDist: number,
    filter?: (id: number) => boolean,
  ): RaycastHit | null {
    const test = filter ?? ((id: number) => isSolid(id) || id === TORCH);

    let x = Math.floor(origin.x);
    let y = Math.floor(origin.y);
    let z = Math.floor(origin.z);

    const stepX = dir.x > 0 ? 1 : -1;
    const stepY = dir.y > 0 ? 1 : -1;
    const stepZ = dir.z > 0 ? 1 : -1;

    const tDeltaX = dir.x === 0 ? Infinity : Math.abs(1 / dir.x);
    const tDeltaY = dir.y === 0 ? Infinity : Math.abs(1 / dir.y);
    const tDeltaZ = dir.z === 0 ? Infinity : Math.abs(1 / dir.z);

    const toBoundary = (o: number, i: number, step: number): number => (step > 0 ? i + 1 - o : o - i);
    let tMaxX = dir.x === 0 ? Infinity : toBoundary(origin.x, x, stepX) * tDeltaX;
    let tMaxY = dir.y === 0 ? Infinity : toBoundary(origin.y, y, stepY) * tDeltaY;
    let tMaxZ = dir.z === 0 ? Infinity : toBoundary(origin.z, z, stepZ) * tDeltaZ;

    // The origin voxel itself may already be a hit.
    const here = this.getBlock(x, y, z);
    if (test(here)) {
      if (here === TORCH) {
        const hitDist = rayIntersectsAABB(
          origin.x, origin.y, origin.z,
          dir.x, dir.y, dir.z,
          x + 0.35, y, z + 0.35,
          x + 0.65, y + 0.65, z + 0.65,
          maxDist,
        );
        if (hitDist !== null) {
          return { x, y, z, nx: 0, ny: 1, nz: 0, block: here, distance: hitDist };
        }
      } else {
        return { x, y, z, nx: 0, ny: 1, nz: 0, block: here, distance: 0 };
      }
    }

    let nx = 0;
    let ny = 0;
    let nz = 0;
    let t = 0;

    while (t <= maxDist) {
      if (tMaxX < tMaxY) {
        if (tMaxX < tMaxZ) {
          x += stepX;
          t = tMaxX;
          tMaxX += tDeltaX;
          nx = -stepX; ny = 0; nz = 0;
        } else {
          z += stepZ;
          t = tMaxZ;
          tMaxZ += tDeltaZ;
          nx = 0; ny = 0; nz = -stepZ;
        }
      } else if (tMaxY < tMaxZ) {
        y += stepY;
        t = tMaxY;
        tMaxY += tDeltaY;
        nx = 0; ny = -stepY; nz = 0;
      } else {
        z += stepZ;
        t = tMaxZ;
        tMaxZ += tDeltaZ;
        nx = 0; ny = 0; nz = -stepZ;
      }

      if (t > maxDist) break;
      if (y < 0 || y >= WORLD_HEIGHT) continue;

      const block = this.getBlock(x, y, z);
      if (test(block)) {
        if (block === TORCH) {
          const hitDist = rayIntersectsAABB(
            origin.x, origin.y, origin.z,
            dir.x, dir.y, dir.z,
            x + 0.35, y, z + 0.35,
            x + 0.65, y + 0.65, z + 0.65,
            maxDist,
          );
          if (hitDist === null) continue;
          return { x, y, z, nx, ny, nz, block, distance: hitDist };
        }
        return { x, y, z, nx, ny, nz, block, distance: t };
      }
    }

    return null;
  }
}

function rayIntersectsAABB(
  ox: number, oy: number, oz: number,
  dx: number, dy: number, dz: number,
  minX: number, minY: number, minZ: number,
  maxX: number, maxY: number, maxZ: number,
  maxDist: number,
): number | null {
  const invDx = dx === 0 ? Infinity : 1 / dx;
  const invDy = dy === 0 ? Infinity : 1 / dy;
  const invDz = dz === 0 ? Infinity : 1 / dz;

  let t1 = (minX - ox) * invDx;
  let t2 = (maxX - ox) * invDx;
  let tMin = Math.min(t1, t2);
  let tMax = Math.max(t1, t2);

  t1 = (minY - oy) * invDy;
  t2 = (maxY - oy) * invDy;
  tMin = Math.max(tMin, Math.min(t1, t2));
  tMax = Math.min(tMax, Math.max(t1, t2));

  t1 = (minZ - oz) * invDz;
  t2 = (maxZ - oz) * invDz;
  tMin = Math.max(tMin, Math.min(t1, t2));
  tMax = Math.min(tMax, Math.max(t1, t2));

  if (tMax < tMin || tMax < 0 || tMin > maxDist) return null;
  return Math.max(0, tMin);
}

export { CHUNK_X, CHUNK_Z, WORLD_HEIGHT, SEA_LEVEL };
